import { z, type ZodType } from 'zod';

export class ConfigError extends Error {
    readonly issues: string[];

    constructor(issues: string[]) {
        super(`Invalid configuration:\n${issues.map((issue) => `  - ${issue}`).join('\n')}`);
        this.name = 'ConfigError';
        this.issues = issues;
    }
}

/**
 * Validates environment variables at startup. A misconfigured service must refuse to start with a clear message,
 * so the error lists every invalid variable at once and never echoes secret values.
 */
export function loadConfig<T>(schema: ZodType<T>, env: NodeJS.ProcessEnv = process.env): T {
    const result = schema.safeParse(env);
    if (result.success) {
        return result.data;
    }
    throw new ConfigError(result.error.issues.map((issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`));
}

export const environmentName = z.enum(['development', 'test', 'staging', 'production']);
export type EnvironmentName = z.infer<typeof environmentName>;

export const logLevel = z.enum(['fatal', 'error', 'warn', 'info', 'debug', 'trace', 'silent']);

/** Reusable env primitives. Booleans are parsed explicitly: `z.coerce.boolean()` would read "false" as true. */
export const env = {
    boolean: (defaultValue: boolean) =>
        z
            .enum(['true', 'false', '1', '0'])
            .default(defaultValue ? 'true' : 'false')
            .transform((value) => value === 'true' || value === '1'),
    port: (defaultValue: number) => z.coerce.number().int().min(1).max(65535).default(defaultValue),
    postgresUrl: () => z.url({ protocol: /^postgres(ql)?$/ }),
    list: () =>
        z
            .string()
            .default('')
            .transform((value) =>
                value
                    .split(',')
                    .map((item) => item.trim())
                    .filter(Boolean),
            ),
};

export const baseConfigSchema = z.object({
    LUMLAB_ENV: environmentName.default('development'),
    LOG_LEVEL: logLevel.default('info'),
});
